import { useState } from "react";
import { withTranslation } from "next-i18next";
import { useCurrentUser } from "@hooks/use-current-user";
import { useReloadUser } from "@hooks/use-modify-user";
import { useCloseDrawer } from "@hooks/use-drawer";
import { useFormatDate } from "@hooks/use-format-date";
import { useRestartSubscription } from "@hooks/use-subscription";
import { Button, Image, Link } from "@components/core";
import { showSuccessToast, showWarningToast } from "@utils/toasts/showToasts";

function DrawerSubscriptionPaused({ t }) {
  const user = useCurrentUser();
  const reloadUser = useReloadUser();
  const closeDrawer = useCloseDrawer();
  const restartSubscription = useRestartSubscription();
  const formatDate = useFormatDate();

  const [loading, setLoading] = useState(false);

  const subscriptionRestartAtDate = user?.subscription?.restart_at;

  function handleSuccess() {
    showSuccessToast(
      t("common:drawer.modal_paused.restart.success.title"),
      t("common:drawer.modal_paused.restart.success.subtitle"),
    );

    reloadUser();
    closeDrawer();
  }

  function handleError() {
    showWarningToast(
      t("common:drawer.modal_paused.restart.error.title"),
      t("common:drawer.modal_paused.restart.error.subtitle"),
    );

    reloadUser();
    closeDrawer();
  }

  async function restartSubscriptionNow() {
    if (loading) return;

    try {
      setLoading(true);
      const subscription = await restartSubscription();

      if (["active", "trialing"].includes(subscription?.state)) {
        handleSuccess();
      } else {
        handleError();
      }
    } catch {
      handleError();
    }
  }

  return [
    <div
      className="drawer-body bg-white p-4"
      key="drawer-subscription-paused-body"
    >
      <div className="p-2 mb-4 text-center">
        <h5 className="mb-2">{t("common:drawer.modal_paused.title")}</h5>
        <p className="mb-0 text-gray">{t("common:drawer.modal_paused.subtitle")}</p>
      </div>
      <div
        style={{ width: "128px" }}
        className="mx-auto"
      >
        <Image
          src="handbagSleepingNeon"
          alt={t("common:drawer.modal_paused.img_alt")}
          className="w-100"
        />
      </div>

      <div className="py-4 m-2">
        <p className="mb-0 text-center text-gray">
          {t("common:drawer.modal_paused.explanation", {
            date: formatDate(subscriptionRestartAtDate),
          })}
        </p>
      </div>
    </div>,
    <div
      className="bg-white d-flex flex-column gap-2 p-3 border-top"
      key="drawer-subscription-paused-footer"
    >
      <Button
        loading={loading}
        onClick={restartSubscriptionNow}
      >
        {t("common:drawer.modal_paused.restart_now")}
      </Button>

      <Link href="/profile/manage-subscription/pause/extend">
        <Button
          fullWidth
          variant="ghost-primary"
        >
          {t("common:drawer.modal_paused.change_pause")}
        </Button>
      </Link>
    </div>,
  ];
}

export default withTranslation("common")(DrawerSubscriptionPaused);
